import { useCallback, useEffect, useState } from "react";

const SIDEBAR_STORAGE_KEY = "sidebarExpanded";
const MOBILE_BREAKPOINT = 768;

function getSavedSidebarState(defaultExpanded) {
  if (typeof window === "undefined") {
    return defaultExpanded;
  }

  const savedState = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);

  if (savedState === null) {
    return defaultExpanded;
  }

  try {
    return JSON.parse(savedState);
  } catch {
    return defaultExpanded;
  }
}

function isMobileViewport() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.innerWidth < MOBILE_BREAKPOINT;
}

export function useSidebarState(defaultExpanded = true) {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() =>
    getSavedSidebarState(defaultExpanded)
  );
  const [isMobile, setIsMobile] = useState(() => isMobileViewport());

  const persistState = useCallback((nextState) => {
    setIsSidebarExpanded(nextState);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(nextState));
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    persistState(!isSidebarExpanded);
  }, [isSidebarExpanded, persistState]);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = isMobileViewport();
      setIsMobile(nextIsMobile);

      if (nextIsMobile && isSidebarExpanded) {
        persistState(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isSidebarExpanded, persistState]);

  return {
    isMobile,
    isSidebarExpanded,
    setSidebarExpanded: persistState,
    toggleSidebar,
  };
}
